import React, { useEffect, useState, useRef } from 'react';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchProducts, createProduct, updateProduct } from '../services/productService';
import { fetchProductCategories } from '../services/productCategoryService';
import { fetchBrands } from '../services/brandService';
import { Modal } from '../components/common';
import { Plus, X } from 'lucide-react';

const shopTypes = ['restaurant', 'grocery', 'pharmacy', 'stationery', 'fancy', 'electronics'];
const unitNameOptions = ['kg', 'gm', 'lt', 'ml', 'pcs', 'piece', 'box', 'bag', 'bundle', 'sachet'];
const emptyVariant = () => ({ name: '', unit: '', unitName: '', sku: '', isActive: true });

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [filters, setFilters] = useState({ search: '', categoryId: '', brandId: '', sortBy: 'created_at', sortOrder: 'DESC' });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const createEmptyForm = () => ({
    name: '',
    sku: '',
    description: '',
    unit: '',
    productCategoryId: '',
    brandId: '',
    variants: [emptyVariant()],
    targetShopTypes: [],
    image: null,
  });

  const [form, setForm] = useState(createEmptyForm());
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});

  const loadData = async () => {
    const { data } = await fetchProducts({ ...filters, page, limit });
    const records = data.data.records || [];
    const meta = data.data.meta || {};
    setProducts(records);
    setTotalPages(meta.pages || 1);
    setTotalRecords(meta.total || records.length);
  };

  useEffect(() => {
    loadData();
  }, [filters, page, limit]);

  useEffect(() => {
    const loadMeta = async () => {
      const [catRes, brandRes] = await Promise.all([
        fetchProductCategories(),
        fetchBrands(),
      ]);
      const catRecords =
        catRes.data.data.records || catRes.data.data || [];
      setCategories(catRecords.filter((c) => c.id !== 0));
      setBrands(brandRes.data.data.records || brandRes.data.data || []);
    };
    loadMeta();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setForm({ ...form, image: file });
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const clearImage = () => {
    setForm((prev) => ({ ...prev, image: null }));
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleVariantChange = (index, field, value) => {
    const updated = [...form.variants];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, variants: updated });
  };

  const addVariant = () => {
    setForm({ ...form, variants: [...form.variants, emptyVariant()] });
  };

  const removeVariant = (index) => {
    setForm({ ...form, variants: form.variants.filter((_, i) => i !== index) });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Product name is required';
    // if (!form.sku.trim()) newErrors.sku = 'SKU is required';
    if (!form.unit.trim()) newErrors.unit = 'Unit is required';
    if (!form.productCategoryId) newErrors.productCategoryId = 'Category is required';
    // if (!form.brandId) newErrors.brandId = 'Brand is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    const payload = {
      ...form,
      variants: form.variants.filter((v) => v.name),
      targetShopTypes: form.targetShopTypes,
    };
    if (editingId) {
      await updateProduct(editingId, payload);
    } else {
      await createProduct(payload);
    }
    setForm(createEmptyForm());
    clearImage();
    setEditingId(null);
    setShowForm(false);
    loadData();
  };

  const startEdit = (p) => {
    setForm({
      name: p.name || '',
      sku: p.sku || '',
      description: p.description || '',
      unit: p.unit || '',
      productCategoryId: p.productCategoryId || '',
      brandId: p.brandId || '',
      variants:
        p.variants && p.variants.length
          ? p.variants.map((v) => ({
              name: v.name || '',
              unit: v.unit || '',
              unitName: (v.unitName || '').toLowerCase(),
              sku: v.sku || '',
              isActive: v.isActive !== undefined ? v.isActive : true,
            }))
          : [emptyVariant()],
      targetShopTypes: p.targetShopTypes ? p.targetShopTypes.map((t) => t.shopType) : [],
      image: null,
    });
    setPreview(p.imageUrl || null);
    if (fileInputRef.current) fileInputRef.current.value = null;
    setEditingId(p.id);
    setShowForm(true);
    setErrors({});
  };

  const openCreate = () => {
    setForm(createEmptyForm());
    clearImage();
    setEditingId(null);
    setShowForm(true);
    setErrors({});
  };

  const closeModal = () => {
    setShowForm(false);
    setForm(createEmptyForm());
    clearImage();
    setEditingId(null);
    setErrors({});
  };

  return (
    <LayoutWithSidebar>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold">Products</h1>
          <button
            className="bg-blue-500 text-white px-4 py-1 flex items-center gap-1"
            onClick={openCreate}
          >
            <Plus size={16} /> Add Product
          </button>
        </div>
        <div className="mb-4 flex flex-wrap gap-2">
          <select
            className="border p-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <input
            type="text"
            placeholder="Search"
            className="border p-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={filters.search}
            onChange={(e) => {
              setFilters({ ...filters, search: e.target.value });
              setPage(1);
            }}
          />
          <select
            className="border p-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={filters.categoryId}
            onChange={(e) => {
              setFilters({ ...filters, categoryId: e.target.value });
              setPage(1);
            }}
          >
            <option value="">All Categories</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select
            className="border p-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={filters.brandId}
            onChange={(e) => {
              setFilters({ ...filters, brandId: e.target.value });
              setPage(1);
            }}
          >
            <option value="">All Brands</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <select
            className="border p-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={filters.sortBy}
            onChange={(e) => {
              setFilters({ ...filters, sortBy: e.target.value });
              setPage(1);
            }}
          >
            <option value="created_at">Created At</option>
            <option value="name">Name</option>
          </select>
          <select
            className="border p-1 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={filters.sortOrder}
            onChange={(e) => {
              setFilters({ ...filters, sortOrder: e.target.value });
              setPage(1);
            }}
          >
            <option value="DESC">Desc</option>
            <option value="ASC">Asc</option>
          </select>
        </div>

        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700 text-left">
              <th className="py-2 px-4 border dark:border-gray-700">Image</th>
              <th className="py-2 px-4 border dark:border-gray-700">Name</th>
              <th className="py-2 px-4 border dark:border-gray-700">Category</th>
              <th className="py-2 px-4 border dark:border-gray-700">Brand</th>
              <th className="py-2 px-4 border dark:border-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-t dark:border-gray-700">
                <td className="border px-4 py-2 dark:border-gray-700">
                  {p.imageUrl && (
                    <img src={p.imageUrl} alt={p.name} className="w-16 h-16 object-cover" />
                  )}
                </td>
                <td className="border px-4 py-2 dark:border-gray-700">{p.name}</td>
                <td className="border px-4 py-2 dark:border-gray-700">{p.category?.name}</td>
                <td className="border px-4 py-2 dark:border-gray-700">{p.brand?.name}</td>
                <td className="border px-4 py-2 dark:border-gray-700">
                  <button
                    className="text-blue-500"
                    onClick={() => startEdit(p)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-4 space-y-2 md:space-y-0">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalRecords)} of {totalRecords} entries
          </div>
          <div className="flex justify-center space-x-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50 dark:border-gray-600"
            >
              Prev
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50 dark:border-gray-600"
            >
              Next
            </button>
          </div>
        </div>

        {showForm && (
          <Modal onClose={closeModal}>
            <h2 className="text-lg font-semibold mb-2">{editingId ? 'Edit' : 'Create'} Product</h2>
            <form onSubmit={handleSubmit} className="space-y-2 max-h-[70vh] overflow-y-auto">
              <label className="block">
                <span className="text-sm">Product Name</span>
                <input
                  className={`border p-1 w-full ${errors.name ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
              </label>
              <label className="block">
                <span className="text-sm">SKU</span>
                <input
                  className={`border p-1 w-full ${errors.sku ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                />
                {errors.sku && <p className="text-red-500 text-sm">{errors.sku}</p>}
              </label>
              <label className="block">
                <span className="text-sm">Description</span>
                <textarea
                  className="border p-1 w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </label>
              <label className="block">
                <span className="text-sm">Unit</span>
                <input
                  className={`border p-1 w-full ${errors.unit ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={form.unit}
                  onChange={(e) => setForm({ ...form, unit: e.target.value })}
                />
                {errors.unit && <p className="text-red-500 text-sm">{errors.unit}</p>}
              </label>
              <label className="block">
                <span className="text-sm">Category</span>
                <select
                  className={`border p-1 w-full ${errors.productCategoryId ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={form.productCategoryId}
                  onChange={(e) => setForm({ ...form, productCategoryId: e.target.value })}
                >
                  <option value="">Select Category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {errors.productCategoryId && (
                  <p className="text-red-500 text-sm">{errors.productCategoryId}</p>
                )}
              </label>
              <label className="block">
                <span className="text-sm">Brand</span>
                <select
                  className={`border p-1 w-full ${errors.brandId ? 'border-red-500' : ''} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                  value={form.brandId}
                  onChange={(e) => setForm({ ...form, brandId: e.target.value })}
                >
                  <option value="">Select Brand</option>
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                {errors.brandId && <p className="text-red-500 text-sm">{errors.brandId}</p>}
              </label>
              <div className="space-y-2">
                {form.variants.map((v, idx) => (
                  <div
                    key={idx}
                    className="border p-2 rounded-md flex flex-wrap items-end gap-2 dark:border-gray-600"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">Name</span>
                      <input
                        className="border p-1 w-40 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={v.name}
                        onChange={(e) => handleVariantChange(idx, 'name', e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm">SKU</span>
                      <input
                        className="border p-1 w-32 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={v.sku}
                        onChange={(e) => handleVariantChange(idx, 'sku', e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm">Unit</span>
                      <input
                        className="border p-1 w-20 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={v.unit}
                        onChange={(e) => handleVariantChange(idx, 'unit', e.target.value)}
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm">Unit Name</span>
                      <select
                        className="border p-1 w-32 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                        value={v.unitName}
                        onChange={(e) =>
                          handleVariantChange(idx, 'unitName', e.target.value.toLowerCase())
                        }
                      >
                        <option value="">Select</option>
                        {unitNameOptions.map((u) => (
                          <option key={u} value={u.toLowerCase()}>{u}</option>
                        ))}
                      </select>
                    </div>
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={v.isActive}
                        onChange={(e) => handleVariantChange(idx, 'isActive', e.target.checked)}
                      />
                      Active
                    </label>
                    <button
                      type="button"
                      className="text-red-500 flex items-center gap-1"
                      onClick={() => removeVariant(idx)}
                    >
                      <X size={16} /> Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addVariant}
                  className="text-blue-500 flex items-center gap-1"
                >
                  <Plus size={16} /> Add Variant
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="text-sm w-full">Target Shop Types</span>
                {shopTypes.map((t) => (
                  <label key={t} className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={form.targetShopTypes.includes(t)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((prev) => ({
                          ...prev,
                          targetShopTypes: checked
                            ? [...prev.targetShopTypes, t]
                            : prev.targetShopTypes.filter((s) => s !== t),
                        }));
                      }}
                    />
                    {t}
                  </label>
                ))}
              </div>
              <label className="block">
                <span className="text-sm">Image</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <div className="flex items-center gap-2 mt-1">
                  <button
                    type="button"
                    className="bg-gray-200 px-2 py-1 dark:bg-gray-700 dark:text-white"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    Upload
                  </button>
                  {preview && (
                    <button
                      type="button"
                      className="px-2 py-1 border dark:border-gray-600"
                      onClick={clearImage}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </label>
              {preview && (
                <img src={preview} alt="Preview" className="w-24 h-24 object-cover" />
              )}
              <div className="flex gap-2">
                <button type="submit" className="bg-blue-500 text-white px-4 py-1">
                  {editingId ? 'Update' : 'Create'}
                </button>
                <button type="button" className="px-4 py-1 border dark:border-gray-600" onClick={closeModal}>
                  Cancel
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </LayoutWithSidebar>
  );
}
